// ============================================
// DashboardSummary — UKA-X01 D8
//
// Stat cards + progress bar for the Team Dashboard.
// Receives pre-computed counts as props — no data fetching.
// Shows "Filtered view" label when isFiltered=true.
//
// Design: DM Sans body, palette matches portal (BG #FAFAFA, cards #FFFFFF).
// Min 16px font, WCAG AA contrast, 48px touch targets.
// ============================================

import React from 'react';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface DashboardSummaryProps {
  total: number;
  reviewed: number;
  approved: number;
  rejected: number;
  changeRequested: number;
  pending: number;
  isFiltered: boolean;
}

// ── Stat card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number;
  accent: string;        // border-left color
  valueColor?: string;   // text color for the number
}

function StatCard({ label, value, accent, valueColor }: StatCardProps) {
  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E4E4E7',
        borderLeft: `4px solid ${accent}`,
        borderRadius: '4px',
        padding: '14px 16px',
        minWidth: '100px',
        flex: '1 1 100px',
      }}
    >
      <div
        style={{
          fontSize: '24px',
          fontWeight: 700,
          color: valueColor ?? '#18181B',
          lineHeight: 1.1,
        }}
        aria-label={`${label}: ${value}`}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '13px',
          color: '#71717A',
          marginTop: '4px',
          fontWeight: 500,
        }}
      >
        {label}
      </div>
    </div>
  );
}

// ── DashboardSummary ──────────────────────────────────────────────────────────

export function DashboardSummary({
  total,
  reviewed,
  approved,
  rejected,
  changeRequested,
  pending,
  isFiltered,
}: DashboardSummaryProps) {
  const pct = total > 0 ? Math.round((reviewed / total) * 100) : 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {/* Filtered view label */}
      {isFiltered && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            color: '#6366F1',
            fontWeight: 600,
            backgroundColor: '#EEF2FF',
            padding: '4px 10px',
            borderRadius: '4px',
            alignSelf: 'flex-start',
          }}
          role="status"
          aria-live="polite"
        >
          <span style={{ fontSize: '11px' }}>&#9670;</span>
          Filtered view
        </div>
      )}

      {/* Stat cards row */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
        }}
        role="region"
        aria-label="Review progress summary"
      >
        <StatCard label="Total" value={total} accent="#A1A1AA" />
        <StatCard label="Approved" value={approved} accent="#16A34A" valueColor="#16A34A" />
        <StatCard label="Rejected" value={rejected} accent="#DC2626" valueColor="#DC2626" />
        <StatCard label="Change Req." value={changeRequested} accent="#D97706" valueColor="#D97706" />
        <StatCard label="Pending" value={pending} accent="#A1A1AA" valueColor="#52525B" />
      </div>

      {/* Progress bar */}
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: '6px',
          }}
        >
          <span style={{ fontSize: '13px', color: '#52525B', fontWeight: 500 }}>
            {reviewed} of {total} reviewed
          </span>
          <span
            style={{
              fontSize: '13px',
              fontWeight: 700,
              color: pct === 100 ? '#16A34A' : '#18181B',
            }}
          >
            {pct}%
          </span>
        </div>
        <div
          style={{
            height: '8px',
            backgroundColor: '#F4F4F5',
            borderRadius: '4px',
            overflow: 'hidden',
          }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${pct}% reviewed`}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              backgroundColor: pct === 100 ? '#16A34A' : '#E11D48',
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
    </div>
  );
}
